import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout/MainLayout';
import apiClient from '../../api/client';
import toast from 'react-hot-toast';
import { Plus, Trash2, Car, AlertCircle, Loader2, ChevronRight } from 'lucide-react';

interface InstallationInstruction {
  id: number;
  partNumber: string;
  notes: string | null;
  createBy: string;
  createDate: string;
  yearStart: number | null;
  yearEnd: number | null;
  makes: string[];
  models: string[];
  vehicleCount: number;
}

interface PartLookupData {
  yearStart: number | null;
  yearEnd: number | null;
  makes: string[];
  models: string[];
  vehicleCount: number;
}

export default function InstallationInstructions() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<InstallationInstruction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Form state
  const [formPartNumber, setFormPartNumber] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [lookupData, setLookupData] = useState<PartLookupData | null>(null);
  const [isLooking, setIsLooking] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      const res: any = await apiClient.get('/installation-instructions');
      setRecords(res.data || res || []);
    } catch {
      toast.error('Failed to load installation instructions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const lookupPart = useCallback(async (partNumber: string) => {
    if (!partNumber.trim()) {
      setLookupData(null);
      setLookupError('');
      return;
    }

    setIsLooking(true);
    setLookupError('');
    setLookupData(null);

    try {
      const res: any = await apiClient.get(`/installation-instructions/lookup/${encodeURIComponent(partNumber.trim())}`);
      const data: PartLookupData = res.data || res;
      if (data.vehicleCount === 0) {
        setLookupError('No vehicle applications found for this part number.');
        setLookupData(null);
      } else {
        setLookupData(data);
      }
    } catch {
      setLookupError('Failed to look up part number.');
    } finally {
      setIsLooking(false);
    }
  }, []);

  const handlePartNumberChange = (value: string) => {
    setFormPartNumber(value);
    setLookupData(null);
    setLookupError('');

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      lookupPart(value);
    }, 500);
  };

  const handleOpenModal = () => {
    setFormPartNumber('');
    setFormNotes('');
    setLookupData(null);
    setLookupError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setShowModal(false);
  };

  const handleSave = async () => {
    if (!formPartNumber.trim()) {
      toast.error('Part number is required');
      return;
    }
    setIsSaving(true);
    try {
      await apiClient.post('/installation-instructions', {
        partNumber: formPartNumber.trim(),
        notes: formNotes.trim() || null,
      });
      toast.success('Installation instruction created');
      handleCloseModal();
      await loadRecords();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number, partNumber: string) => {
    if (!window.confirm(`Delete installation instruction for "${partNumber}"?`)) return;
    setDeletingId(id);
    try {
      await apiClient.delete(`/installation-instructions/${id}`);
      toast.success('Deleted');
      setRecords(prev => prev.filter(r => r.id !== id));
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const formatYearRange = (start: number | null, end: number | null) => {
    if (!start && !end) return '—';
    if (start === end) return `${start}`;
    return `${start} – ${end}`;
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <MainLayout>
      <div className="w-full">
        <div className="bg-white dark:bg-gray-800 shadow">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Installation Instructions</h1>
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                Manage installation instruction records by part number
              </p>
            </div>
            <button
              onClick={handleOpenModal}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Installation
            </button>
          </div>

          {/* Table */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading...
              </div>
            ) : records.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
                <Car className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">No installation instructions yet.</p>
                <p className="text-xs mt-1">Click "New Installation" to add one.</p>
              </div>
            ) : (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 text-left">
                    <tr>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Part Number</th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Year Range</th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Make(s)</th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Model(s)</th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Notes</th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Created</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {records.map(record => (
                      <tr key={record.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750">
                        <td className="px-4 py-3">
                          <button
                            onClick={() => navigate(`/admin/installation-instructions/${record.id}`)}
                            className="flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                          >
                            {record.partNumber}
                            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {formatYearRange(record.yearStart, record.yearEnd)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {record.makes.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {record.makes.map(make => (
                                <span key={make} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                  {make}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs">
                          {record.models.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {record.models.slice(0, 4).map(model => (
                                <span key={model} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                  {model}
                                </span>
                              ))}
                              {record.models.length > 4 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                  +{record.models.length - 4} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-xs truncate">
                          {record.notes || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {formatDate(record.createDate)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDelete(record.id, record.partNumber)}
                            disabled={deletingId === record.id}
                            className="p-1.5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-40"
                            title="Delete"
                          >
                            {deletingId === record.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Trash2 className="w-4 h-4" />
                            }
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Installation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New Installation Instruction</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Part Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Part Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formPartNumber}
                  onChange={(e) => handlePartNumberChange(e.target.value)}
                  placeholder="Enter part number..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              {/* Vehicle Data Panel */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Vehicle Applications
                  </span>
                </div>
                <div className="px-4 py-4">
                  {isLooking ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Looking up vehicle data...
                    </div>
                  ) : lookupError ? (
                    <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {lookupError}
                    </div>
                  ) : lookupData ? (
                    <div className="space-y-3">
                      {/* Year Range */}
                      <div className="flex gap-6">
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Year Start</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {lookupData.yearStart ?? '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Year End</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {lookupData.yearEnd ?? '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Fitments</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {lookupData.vehicleCount}
                          </p>
                        </div>
                      </div>

                      {/* Makes */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Make(s)</p>
                        <div className="flex flex-wrap gap-1.5">
                          {lookupData.makes.map(make => (
                            <span key={make} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                              {make}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Models */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Model(s)</p>
                        <div className="flex flex-wrap gap-1.5">
                          {lookupData.models.map(model => (
                            <span key={model} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                              {model}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                      Enter a part number above to load vehicle data.
                    </p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Notes <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Add any notes..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !formPartNumber.trim()}
                className="flex items-center gap-2 px-5 py-2 text-sm bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
